import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices, useUploadImage } from "../../../../hook/services";

const LogoUpload = ({ hederText }) => {
  const AppService = useAppServices();
  const uploadImage = useUploadImage();
  const { type, companyId, customizationId } = useParams();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  let query =
    type === "location" || type === "agency"
      ? `companyId=${companyId}&type=${type}`
      : `_id=${customizationId}`;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [logo, setLogo] = useState(null);
  const [agencyLogo, setAgencyLogo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const getAgency = async () => {
    const { response } = await AppService.agency.get({
      query: `companyAuth.companyId=${companyId}`,
    });
    if (response && response.data?.companyAuth) {
      const fallback = response.data?.companyAuth?.company?.logoUrl || null;
      setAgencyLogo(fallback);
      setLogo((prev) => prev || fallback);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { response } = await handler.filter({ query: query });
      if (response?.data) {
        setData(response.data);
        if (response.data?.logo) {
          setLogo(response.data?.logo);
        }
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const onLoad = () => {
    getAgency();
    fetchData();
  };

  useEffect(onLoad, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
  };

  const handleRemove = async () => {
    if (!data?._id) return;
    setRemoving(true);
    try {
      const payload = { type, logo: "", _id: data._id };
      const { response } = await handler.update({ payload });
      if (response) {
        setLogo(agencyLogo || null);
        setData((prev) => ({ ...prev, logo: "" }));
        setPreview(null);
      }
    } catch (err) {
      console.error("Error removing logo:", err);
    } finally {
      setRemoving(false);
    }
  };

  const handleUpload = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      const file = document.getElementById("logoInput").files[0];
      let Image = null;
      if (file) {
        const image_response = await uploadImage({
          file: file,
          desiredPath: `customizer/logo`,
        });
        if (image_response.response) {
          Image = image_response.response.data;
        }
        if (Image) {
          const payload = {
            type,
            logo: Image,
            ...(data?._id && { _id: data._id }),
          };
          const { response } = await handler[data?._id ? "update" : "create"]({
            payload,
          });
          if (response) {
            fetchData();
            setPreview(null);
          }
        }
      }
    } catch (err) {
      console.error("Error uploading logo:", err);
    } finally {
      setUploading(false);
    }
  };

  const displaySrc = preview || logo;
  const hasPendingPreview = !!preview;

  return (
    <section className="logo-upload-card">
      <div className="d-flex align-items-center flex-wrap gap-4">
        {/* Logo */}
        <label
          htmlFor="logoInput"
          className="logo-thumb position-relative flex-shrink-0"
          title="Click to change logo"
        >
          {displaySrc ? (
            <img
              src={displaySrc}
              alt="Company logo"
              className="logo-thumb-img"
            />
          ) : (
            <div className="logo-thumb-placeholder d-flex align-items-center justify-content-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <circle cx="9" cy="9" r="2" />
                <path d="m21 15-4.586-4.586a2 2 0 0 0-2.828 0L3 21" />
              </svg>
            </div>
          )}

          <span className="logo-thumb-overlay d-flex align-items-center justify-content-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
              <circle cx="12" cy="13" r="3.2" />
            </svg>
          </span>
        </label>

        {/* Content */}
        <div className="flex-grow-1" style={{ minWidth: 240 }}>
          <h2 className="logo-upload-title mb-1">UI Configuration</h2>
          {hederText && <p className="logo-upload-subtitle mb-3">{hederText}</p>}

          {!hasPendingPreview ? (
            <div className="d-flex gap-2 flex-wrap">
              <label htmlFor="logoInput" className="btn-min btn-min--ghost">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {logo ? "Change logo" : "Upload logo"}
              </label>
              {data?.logo && (
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={removing}
                  className="btn-min btn-min--ghost btn-min--danger"
                >
                  {removing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      Removing
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                      Remove
                    </>
                  )}
                </button>
              )}
            </div>
          ) : (
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading}
                className="btn-min btn-min--primary"
              >
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Uploading
                  </>
                ) : (
                  "Save changes"
                )}
              </button>
              <button
                type="button"
                onClick={() => setPreview(null)}
                disabled={uploading}
                className="btn-min btn-min--ghost"
              >
                Cancel
              </button>
              <span className="logo-upload-hint ms-1">Preview shown — save to apply.</span>
            </div>
          )}

          <input
            id="logoInput"
            type="file"
            accept="image/*"
            className="d-none"
            onChange={handleFileChange}
          />
        </div>
      </div>

      {loading && (
        <div className="logo-upload-loading-bar" aria-hidden="true">
          <span />
        </div>
      )}

      <style>{`
        .logo-upload-card {
          position: relative;
          border: 1px solid #ececec;
          border-radius: 12px;
          padding: 20px 22px;
          background: #fff;
          overflow: hidden;
        }

        .logo-thumb {
          width: 72px;
          height: 72px;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          display: block;
          background: #f6f6f6;
          border: 1px solid #ececec;
        }
        .logo-thumb-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .logo-thumb-placeholder {
          width: 100%;
          height: 100%;
          color: #b5b5b5;
        }
        .logo-thumb-overlay {
          position: absolute;
          inset: 0;
          background: rgba(31, 41, 55, 0.55);
          color: #fff;
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        .logo-thumb:hover .logo-thumb-overlay,
        .logo-thumb:focus-within .logo-thumb-overlay {
          opacity: 1;
        }

        .logo-upload-title {
          font-size: 1.0625rem;
          font-weight: 600;
          letter-spacing: -0.005em;
          color: #1f2937;
        }
        .logo-upload-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.5;
        }
        .logo-upload-hint {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .btn-min {
          display: inline-flex;
          align-items: center;
          font-size: 0.8125rem;
          font-weight: 500;
          padding: 7px 12px;
          border-radius: 7px;
          line-height: 1;
          border: 1px solid transparent;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
          cursor: pointer;
        }
        .btn-min:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-min:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
        }

        .btn-min--ghost {
          background: #fff;
          border-color: #e2e2e2;
          color: #1f2937;
        }
        .btn-min--ghost:hover:not(:disabled) {
          background: #f7f7f7;
          border-color: #d4d4d4;
        }
        .btn-min--danger {
          color: #b91c1c;
        }
        .btn-min--danger:hover:not(:disabled) {
          background: #fef2f2;
          border-color: #fecaca;
          color: #991b1b;
        }

        .btn-min--primary {
          background: #4f46e5;
          color: #fff;
          border-color: #4f46e5;
        }
        .btn-min--primary:hover:not(:disabled) {
          background: #4338ca;
          border-color: #4338ca;
        }

        .logo-upload-loading-bar {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 2px;
          background: transparent;
          overflow: hidden;
        }
        .logo-upload-loading-bar span {
          display: block;
          height: 100%;
          width: 30%;
          background: #4f46e5;
          animation: logoUploadLoading 1.1s ease-in-out infinite;
        }
        @keyframes logoUploadLoading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(420%); }
        }
      `}</style>
    </section>
  );
};

export default LogoUpload;
